import { managementDraft, managementSaved } from '../utils/managementDraft';
import { useEffect, useMemo, useState, useRef } from "react";
import "./leads.css";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import ConfirmationModal from "./confirmationModal";
import Checkbox from "@mui/material/Checkbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faChevronDown, faChevronLeft, faChevronRight, faCircle, faEnvelope, faPhone, faTag, faUser, faTrash, faSpinner, faDownload } from "@fortawesome/free-solid-svg-icons";

dayjs.extend(utc);
dayjs.extend(timezone);

const LEADS_PER_PAGE = 25;

const getUserDateFormat = () => {
  const stored = localStorage.getItem("date_format");
  if (stored === "YYYY-MM-DD" || stored === "DD-MM-YYYY" || stored === "MM-DD-YYYY") {
    return stored;
  }
  return "YYYY-MM-DD";
};

const getUserTimezone = () => {
  return localStorage.getItem("timezone") || "UTC";
};

function formatDateNice(iso) {
  if (!iso) return "";
  const d = dayjs.utc(iso).tz(getUserTimezone());
  if (!d.isValid()) return iso;
  const pattern = getUserDateFormat();
  return d.format(pattern + " HH:mm");
}

const formatDateHeader = (dateStr, t) => {
  const tz = getUserTimezone();
  const date = dayjs.utc(dateStr).tz(tz).toDate();
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  const isToday = date.toDateString() === now.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();

  if (isToday) return t('today') || 'Today';
  if (isYesterday) return t('yesterday') || 'Yesterday';

  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
};

const formatMessageTime = (timestamp) => {
  return dayjs.utc(timestamp).tz(getUserTimezone()).format('HH:mm');
};

const getDateKey = (timestamp) => {
  return dayjs.utc(timestamp).tz(getUserTimezone()).format('YYYY-MM-DD');
};

const normalizeLeadChatHistory = (chatHistory) => {
  const messages = [];
  for (const item of chatHistory || []) {
    if (item?.type && item?.content) {
      messages.push(item);
      continue;
    }
    if (item?.user_input) {
      messages.push({
        type: "user_input",
        content: item.user_input,
        timestamp: item.user_input_timestamp,
      });
    }
    if (item?.assistant) {
      messages.push({
        type: "assistant",
        content: item.assistant,
        timestamp: item.assistant_timestamp,
      });
    }
  }
  return messages;
};

const LinkRenderer = (props) => (
  <a href={props.href} target="_blank" rel="noopener noreferrer">
    {props.children}
  </a>
);

const Leads = ({ bots = [], selectedBotIds = [], setUnreadLeadsCount, startDate, endDate, setStartDate, setEndDate }) => {
  const { t } = useTranslation();
  const [leads, setLeads] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [chatHistoryLoading, setChatHistoryLoading] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  // Track if we've initialized dates from data
  const datesInitializedRef = useRef(false);
  const selectAllRef = useRef(null);

  const fetchLeads = async () => {
    if (!selectedBotIds || selectedBotIds.length === 0) return;

    setLoading(true);
    setError("");
    try {
      const queryParts = selectedBotIds
        .filter(Boolean)
        .map((id) => `bot_id=${encodeURIComponent(id)}`);

      // Add date filters if set
      if (startDate) {
        queryParts.push(`start_date=${startDate}`);
      }
      if (endDate) {
        queryParts.push(`end_date=${endDate}`);
      }

      const query = queryParts.join("&") || "";

      const url = `${process.env.REACT_APP_API_BASE_URL}leads${query ? `?${query}` : ""}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const data = await res.json();
      setLeads(data || []);
      setSelectedLeadIds((prev) => {
        const visibleIds = new Set((data || []).map((lead) => lead.id));
        return prev.filter((id) => visibleIds.has(id));
      });

      // Calculate and set initial date range from data (only once)
      if (!datesInitializedRef.current && data && data.length > 0 && setStartDate && setEndDate) {
        const timestamps = data.map(lead => new Date(lead.date));
        const minDate = new Date(Math.min(...timestamps));
        const maxDate = new Date(Math.max(...timestamps));
        const today = new Date();

        // Subtract 1 day from min date to ensure we fetch all
        minDate.setDate(minDate.getDate() - 1);

        // End date is today or the latest lead date, whichever is later
        const endDateValue = maxDate > today ? maxDate : today;

        setStartDate(dayjs(minDate).format('YYYY-MM-DD'));
        setEndDate(dayjs(endDateValue).format('YYYY-MM-DD'));
        datesInitializedRef.current = true;
      }
    } catch (err) {
      console.error("Error fetching leads:", err);
      setError(t("leads_load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedBotIds), startDate, endDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [JSON.stringify(selectedBotIds), startDate, endDate]);

  // Reset dates initialization when bot selection changes
  useEffect(() => {
    datesInitializedRef.current = false;
  }, [JSON.stringify(selectedBotIds)]);

  const markAsRead = async (leadId) => {
    try {
      const draft = managementDraft();
      const current = await fetch(`${process.env.REACT_APP_API_BASE_URL}management-leads/${leadId}`);
      if (!current.ok && ![404, 503].includes(current.status)) throw new Error('Could not load lead.');
      draft.loaded(leadId, current.ok ? await current.json() : {});
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}leads/${leadId}`,
        draft.request(leadId, { read: true })
      );
      await managementSaved(res, draft, leadId, t);
    } catch (err) {
      console.error(`Error updating lead ${leadId}:`, err);
    }
  };

  const openDeleteModal = (lead, e) => {
    if (e) e.stopPropagation();
    setLeadToDelete(lead);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!leadToDelete) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}leads/${leadToDelete.id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      setLeads((prev) => {
        const updated = prev.filter((l) => l.id !== leadToDelete.id);
        const unreadCount = updated.filter((l) => !l.read).length;
        if (setUnreadLeadsCount) setUnreadLeadsCount(unreadCount);
        return updated;
      });
      setExpanded((prev) => {
        const copy = { ...prev };
        delete copy[leadToDelete.id];
        return copy;
      });
      setSelectedLeadIds((prev) => prev.filter((id) => id !== leadToDelete.id));
    } catch (err) {
      console.error(`Error deleting lead ${leadToDelete.id}:`, err);
      setError(t("leads_delete_failed"));
    } finally {
      setLeadToDelete(null);
      setDeleteOpen(false);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedLeadIds.length === 0) return;
    const idsToDelete = [...selectedLeadIds];
    try {
      const responses = await Promise.all(
        idsToDelete.map((leadId) =>
          fetch(`${process.env.REACT_APP_API_BASE_URL}leads/${leadId}`, { method: "DELETE" })
        )
      );
      const failed = responses.find((res) => !res.ok);
      if (failed) throw new Error(`HTTP error! Status: ${failed.status}`);

      const idsToDeleteSet = new Set(idsToDelete);
      setLeads((prev) => {
        const updated = prev.filter((lead) => !idsToDeleteSet.has(lead.id));
        const unreadCount = updated.filter((lead) => !lead.read).length;
        if (setUnreadLeadsCount) setUnreadLeadsCount(unreadCount);
        return updated;
      });
      setExpanded((prev) => {
        const copy = { ...prev };
        idsToDelete.forEach((id) => delete copy[id]);
        return copy;
      });
      setSelectedLeadIds([]);
      setBulkDeleteOpen(false);
    } catch (err) {
      console.error("Error deleting selected leads:", err);
      setError(t("leads_bulk_delete_failed"));
    }
  };

  const fetchChatHistory = async (sessionId, leadId) => {
    setChatHistoryLoading((prev) => ({ ...prev, [leadId]: true }));
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}chat_history?session_id=${encodeURIComponent(sessionId)}`
      );
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
      const data = await res.json();
      setLeads((prev) =>
        prev.map((l) =>
          l.id === leadId ? { ...l, chat_history: data } : l
        )
      );
    } catch (err) {
      console.error(`Error fetching chat history for lead ${leadId}:`, err);
    } finally {
      setChatHistoryLoading((prev) => ({ ...prev, [leadId]: false }));
    }
  };

  const toggleExpand = (leadId) => {
    const lead = leads.find((l) => l.id === leadId);
    const isCurrentlyExpanded = expanded[leadId];

    setExpanded((prev) => ({
      ...prev,
      [leadId]: !prev[leadId],
    }));

    // Lazy load chat history when expanding (if not already loaded)
    if (!isCurrentlyExpanded && lead && !lead.chat_history) {
      fetchChatHistory(lead.session_id, leadId);
    }

    setLeads((prev) => {
      const updated = prev.map((l) => (l.id === leadId ? { ...l, read: true } : l));
      const unreadCount = updated.filter((l) => !l.read).length;
      if (setUnreadLeadsCount) setUnreadLeadsCount(unreadCount);
      return updated;
    });

    markAsRead(leadId);
  };

  const handleRowKeyDown = (e, leadId) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggleExpand(leadId);
    }
  };

  const totalPages = Math.max(1, Math.ceil(leads.length / LEADS_PER_PAGE));
  const activePage = Math.min(currentPage, totalPages);
  const paginationPages = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = [1];
    const start = Math.max(2, activePage - 1);
    const end = Math.min(totalPages - 1, activePage + 1);

    if (start > 2) pages.push("start-ellipsis");
    for (let page = start; page <= end; page += 1) pages.push(page);
    if (end < totalPages - 1) pages.push("end-ellipsis");
    pages.push(totalPages);

    return pages;
  }, [activePage, totalPages]);
  const visibleLeads = useMemo(() => {
    const startIndex = (activePage - 1) * LEADS_PER_PAGE;
    return leads.slice(startIndex, startIndex + LEADS_PER_PAGE);
  }, [leads, activePage]);
  const visibleLeadIds = useMemo(() => visibleLeads.map((lead) => lead.id), [visibleLeads]);
  const selectedLeadIdsSet = useMemo(() => new Set(selectedLeadIds), [selectedLeadIds]);
  const visibleSelectedCount = visibleLeadIds.filter((id) => selectedLeadIdsSet.has(id)).length;
  const allSelected = visibleLeadIds.length > 0 && visibleSelectedCount === visibleLeadIds.length;
  const someSelected = visibleSelectedCount > 0 && !allSelected;

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      const visibleIds = new Set(visibleLeadIds);
      setSelectedLeadIds((prev) => prev.filter((id) => !visibleIds.has(id)));
    } else {
      setSelectedLeadIds((prev) => Array.from(new Set([...prev, ...visibleLeadIds])));
    }
  };

  const toggleSelectedLead = (leadId) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId) ? prev.filter((id) => id !== leadId) : [...prev, leadId]
    );
  };

  // Export leads to an Excel file. Generated server-side (mirrors the Inbox
  // download) so it scales to large sets and can include every captured field,
  // not just what this list view loads. The same agent + date-range filters
  // are forwarded, so the export matches what's on screen.
  const exportLeads = async () => {
    if (exporting || !selectedBotIds || selectedBotIds.length === 0) return;
    setExporting(true);
    try {
      const parts = selectedBotIds
        .filter(Boolean)
        .map((id) => `bot_id=${encodeURIComponent(id)}`);
      if (startDate) parts.push(`start_date=${startDate}`);
      if (endDate) parts.push(`end_date=${endDate}`);

      const url = `${process.env.REACT_APP_API_BASE_URL}leads_excel${parts.length ? `?${parts.join("&")}` : ""}`;
      const res = await fetch(url, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

      const contentDisposition = res.headers.get("Content-Disposition");
      const filename =
        (contentDisposition &&
          contentDisposition.split("filename=")[1]?.trim().replace(/"/g, "")) ||
        `leads_${dayjs().format("YYYY-MM-DD")}.xlsx`;

      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error exporting leads:", err);
      setError(t("leads_export_failed"));
    } finally {
      setExporting(false);
    }
  };

  const renderParamChips = (params) => (
    <div className="lead-param-chips">
      {params?.name && (
        <span className="param-chip" title={`name: ${params.name}`}>
          <FontAwesomeIcon icon={faUser} className="param-chip-icon" />
          <span className="param-chip-key">name</span>
          <span className="param-chip-sep">•</span>
          <span className="param-chip-val">{params.name}</span>
        </span>
      )}
      {params?.email && (
        <span className="param-chip" title={`email: ${params.email}`}>
          <FontAwesomeIcon icon={faEnvelope} className="param-chip-icon" />
          <span className="param-chip-key">email</span>
          <span className="param-chip-sep">•</span>
          <a
            href={`mailto:${params.email}`}
            className="param-chip-val"
            onClick={(e) => e.stopPropagation()}
          >
            {params.email}
          </a>
        </span>
      )}
      {params?.phone && (
        <span className="param-chip" title={`phone: ${params.phone}`}>
          <FontAwesomeIcon icon={faPhone} className="param-chip-icon" />
          <span className="param-chip-key">phone</span>
          <span className="param-chip-sep">•</span>
          <a
            href={`tel:${params.phone.replace(/\s/g, "")}`}
            className="param-chip-val"
            onClick={(e) => e.stopPropagation()}
          >
            {params.phone}
          </a>
        </span>
      )}
      {/* Custom params: leads carry an open params object (whatever the owner's
          skill collects — company name, budget, ...), so every key beyond the
          three known chips above gets a generic chip. */}
      {Object.entries(params || {})
        .filter(([key, value]) =>
          !["name", "email", "phone"].includes(key) &&
          value !== null && value !== undefined && String(value).trim() !== ""
        )
        .map(([key, value]) => {
          const label = key.replace(/_/g, " ");
          const text = typeof value === "object" ? JSON.stringify(value) : String(value);
          return (
            <span key={key} className="param-chip" title={`${label}: ${text}`}>
              <FontAwesomeIcon icon={faTag} className="param-chip-icon" />
              <span className="param-chip-key">{label}</span>
              <span className="param-chip-sep">•</span>
              <span className="param-chip-val">{text}</span>
            </span>
          );
        })}
    </div>
  );

  const renderChat = (lead) => {
    if (chatHistoryLoading[lead.id]) {
      return (
        <div className="lead-chat-loading">
          <FontAwesomeIcon icon={faSpinner} spin /> {t("leads_loading_chat_history")}
        </div>
      );
    }
    if (!lead.chat_history || !lead.chat_history.length) {
      return <div className="lead-chat-empty">{t("leads_no_chat_history")}</div>;
    }

    const messages = normalizeLeadChatHistory(lead.chat_history);
    let lastDateKey = null;

    return (
      <div className="lead-chat-thread mirror-card">
        {messages.map((chat, index) => {
          const currentDateKey = chat.timestamp ? getDateKey(chat.timestamp) : null;
          const showDateSeparator = currentDateKey && currentDateKey !== lastDateKey;
          if (currentDateKey) lastDateKey = currentDateKey;

          return (
            <div key={index}>
              {showDateSeparator && (
                <div className="chat-date-separator" data-date={currentDateKey}>
                  <span className="chat-date-separator-text">{formatDateHeader(chat.timestamp, t)}</span>
                </div>
              )}
              <div
                className={`chat-message ${
                  chat.type === "user_input" ? "user-history-message" : "assistant-message"
                }`}
              >
                {chat.type === "user_input" ? (
                  <div className="user-message-group">
                    <div className="user-message-body">
                      <div className="message-bubble-history">
                        <ReactMarkdown
                          components={{ a: LinkRenderer }}
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeRaw]}
                        >
                          {chat.content?.replace(/  +/g, ' ')?.replace(/ $/gm, '')}
                        </ReactMarkdown>
                      </div>
                    </div>
                    {chat.timestamp && (
                      <span className="message-time" style={{ marginTop: 0 }}>{formatMessageTime(chat.timestamp)}</span>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="message-bubble-history">
                      <ReactMarkdown
                        components={{ a: LinkRenderer }}
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {chat.content?.replace(/  +/g, ' ')?.replace(/ $/gm, '')}
                      </ReactMarkdown>
                    </div>
                    {chat.timestamp && (
                      <span className="message-time">{formatMessageTime(chat.timestamp)}</span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="leads-error">
        <div className="error-card">{error}</div>
      </div>
    );
  }

  return (
    <div className="leads-root-min">
      {/* Toolbar always renders so Export stays visible even with no leads
          (it just fades to its disabled state). Select-all + bulk delete only
          make sense once there are leads, so they stay gated. */}
      <div className="lead-select-all-row">
        {visibleLeads.length > 0 && (
          <>
            <Checkbox
              inputRef={selectAllRef}
              checked={allSelected}
              indeterminate={someSelected}
              onChange={toggleSelectAll}
              size="small"
              sx={{ padding: 0, marginRight: '8px', color: '#5616ea', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' } }}
            />
            <span className="lead-select-all-label">{t('select_all') || 'Select all'}</span>
          </>
        )}
        <div className="lead-bulk-actions">
          {selectedLeadIds.length > 0 && (
            <span className="lead-bulk-count">
              {selectedLeadIds.length} {t('selected') || 'selected'}
            </span>
          )}
          <button
            className="lead-export-button"
            onClick={exportLeads}
            disabled={leads.length === 0 || exporting}
            title={t('export') || 'Export'}
          >
            {t('export') || 'Export'}
            <FontAwesomeIcon
              icon={exporting ? faSpinner : faDownload}
              spin={exporting}
              className="lead-export-icon"
            />
          </button>
          {visibleLeads.length > 0 && (
            <button
              className={`lead-bulk-delete-button${selectedLeadIds.length === 0 ? ' lead-bulk-delete-muted' : ''}`}
              onClick={() => setBulkDeleteOpen(true)}
              disabled={selectedLeadIds.length === 0}
              title={t('remove') || 'Remove'}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      </div>

      {visibleLeads.map((lead) => {
        const isExpanded = expanded[lead.id];
        const itemName =
          lead?.params?.email || lead?.params?.name || t("leads_item_fallback", { id: lead.id });

        return (
          <div key={lead.id} className={`lead-card ${isExpanded ? "expanded" : ""}`}>
            {/* Header row (entire row clickable) */}
            <div
              className="lead-row"
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onClick={() => toggleExpand(lead.id)}
              onKeyDown={(e) => handleRowKeyDown(e, lead.id)}
            >
              <div
                className="lead-col select"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Checkbox
                  checked={selectedLeadIdsSet.has(lead.id)}
                  onChange={() => toggleSelectedLead(lead.id)}
                  size="small"
                  sx={{ padding: 0, marginRight: '4px', color: '#5616ea', '&.Mui-checked': { color: '#5616ea' } }}
                />
              </div>

              <div className="lead-col status">
                <span className={`badge ${lead.read ? "read" : "unread"}`}>
                  <FontAwesomeIcon icon={faCircle} />
                  {lead.read ? (t("read") || "Read") : (t("unread") || "Unread")}
                </span>
              </div>

              <div className="lead-col bot">
                <span className="bot-pill">{lead.bot_name}</span>
                {/* forwarded during an Agent-Studio test conversation */}
                {String(lead.session_id || '').startsWith('studio-test-') && (
                  <span className="lead-test-pill">{t('agent_studio_test_badge')}</span>
                )}
              </div>

              <div className="lead-col when">
                <span className="date">
                  <FontAwesomeIcon icon={faCalendar} className="date-icon" />
                  {formatDateNice(lead.date)}
                </span>
              </div>

              <div className="lead-col params">{renderParamChips(lead.params)}</div>

              <div className="lead-col action" onClick={(e) => e.stopPropagation()}>
                <div className="actions-wrap">
                  <button
                    className={`chevron-btn ${isExpanded ? "expanded" : ""}`}
                    onClick={() => toggleExpand(lead.id)}
                    aria-label={isExpanded ? t("collapse") : t("expand")}
                  >
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>

                  <button
                    className="trash-btn"
                    title={t("remove") || "Remove"}
                    onClick={(e) => openDeleteModal(lead, e)}
                    aria-label={t("leads_delete_aria")}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>

            {isExpanded && renderChat(lead)}

            {/* Delete confirmation modal */}
            {leadToDelete?.id === lead.id && (
              <ConfirmationModal
                open={deleteOpen}
                onClose={() => {
                  setDeleteOpen(false);
                  setLeadToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                itemName={itemName}
                requireTextConfirmation={false}
              />
            )}
          </div>
        );
      })}

      {visibleLeads.length === 0 && !loading && (
        <div className="leads-empty-min">
          <div className="empty-card">{t("leads_empty")}</div>
        </div>
      )}

      {leads.length > LEADS_PER_PAGE && (
        <div className="lead-pagination" aria-label={t("leads_pagination_aria")}>
          <div className="lead-pagination-controls">
            <button
              className="lead-page-button lead-page-arrow"
              type="button"
              onClick={() => goToPage(activePage - 1)}
              disabled={activePage === 1}
              aria-label={t("pagination_prev")}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            {paginationPages.map((page) => (
              typeof page === "number" ? (
                <button
                  key={page}
                  className={`lead-page-button${activePage === page ? " active" : ""}`}
                  type="button"
                  onClick={() => goToPage(page)}
                  aria-current={activePage === page ? "page" : undefined}
                >
                  {page}
                </button>
              ) : (
                <span key={page} className="lead-page-ellipsis">...</span>
              )
            ))}
            <button
              className="lead-page-button lead-page-arrow"
              type="button"
              onClick={() => goToPage(activePage + 1)}
              disabled={activePage === totalPages}
              aria-label={t("pagination_next")}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
          <div className="lead-pagination-summary">
            {t("leads_pagination_summary", {
              from: ((activePage - 1) * LEADS_PER_PAGE) + 1,
              to: Math.min(activePage * LEADS_PER_PAGE, leads.length),
              total: leads.length,
            })}
          </div>
        </div>
      )}

      <ConfirmationModal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        itemName={`${selectedLeadIds.length} ${t('selected') || 'selected'}`}
        requireTextConfirmation={false}
      />
    </div>
  );
};

export default Leads;
